/**
 * Shared positioned markers for arcade playfields.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";

export interface ArcadePlayfieldMarkerProps {
  readonly children?: React.ReactNode;
  readonly as?: "button" | "div";
  readonly x: number | string;
  readonly y?: number | string;
  readonly bottom?: number | string;
  readonly anchor?: "center" | "bottom";
  readonly ariaLabel?: string;
  readonly ariaHidden?: boolean;
  readonly disabled?: boolean;
  readonly onClick?: () => void;
  readonly className?: string;
  readonly borderColor?: string;
  readonly background?: string;
  readonly color?: string;
  readonly boxShadow?: string;
  readonly transition?: React.CSSProperties["transition"];
  readonly style?: React.CSSProperties;
}

export function ArcadePlayfieldMarker({
  children,
  as = "div",
  x,
  y,
  bottom,
  anchor = "center",
  ariaLabel,
  ariaHidden,
  disabled = false,
  onClick,
  className,
  borderColor,
  background,
  color,
  boxShadow,
  transition,
  style,
}: ArcadePlayfieldMarkerProps): React.JSX.Element {
  const markerClassName = joinClassNames(
    "absolute",
    anchor === "bottom"
      ? "-translate-x-1/2 -translate-y-full"
      : "-translate-x-1/2 -translate-y-1/2",
    className,
  );
  const markerStyle: React.CSSProperties = {
    background,
    borderColor,
    bottom: bottom === undefined ? undefined : positionValue(bottom),
    boxShadow,
    color,
    left: positionValue(x),
    top: y === undefined ? undefined : positionValue(y),
    transition,
    ...style,
  };

  if (as === "button") {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
      aria-hidden={ariaHidden}
      disabled={disabled}
      onClick={onClick}
        className={markerClassName}
        style={markerStyle}
      >
        {children}
      </button>
    );
  }

  return (
    <div
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      className={markerClassName}
      style={markerStyle}
    >
      {children}
    </div>
  );
}

export function arcadeLaneCenter(index: number, count: number): string {
  return `${((index + 0.5) / count) * 100}%`;
}

export function arcadeOffsetLaneCenter(
  index: number,
  count: number,
  offset: number,
): string {
  const center = arcadeLaneCenter(index, count);
  if (offset === 0) return center;
  const operator = offset > 0 ? "+" : "-";
  return `calc(${center} ${operator} ${String(Math.abs(offset))}%)`;
}

export function arcadeScalePosition(value: number, total: number): string {
  return `${(value / total) * 100}%`;
}

function positionValue(value: number | string): string {
  return typeof value === "number" ? `${value}%` : value;
}

function joinClassNames(...classNames: readonly (string | undefined)[]): string {
  return classNames.filter(Boolean).join(" ");
}
